import React, { useEffect, useState, useRef } from "react";
import { getAverageProcessingTime } from "../services/processingTimes";

const MIN_SAMPLES = 10; // show avg only when we have 10 or more

export default function ProcessingTime({ promptType, status, startTimestamp }) {
  // State for UI display
  const [avg, setAvg] = useState(null);
  const [hasData, setHasData] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  
  // Use refs to ensure stable values that don't change across re-renders
  const timerIdRef = useRef(null);
  const startTimeRef = useRef(null);
  
  // Initialize startTimeRef only once
  if (startTimeRef.current === null) {
    startTimeRef.current = startTimestamp || Date.now();
    console.log(`[ProcessingTime-${promptType}] Initialized with timestamp: ${startTimeRef.current}`);
  }
  
  // Set up timer and fetch average times
  useEffect(() => {
    // Only run when status is 'processing'
    if (status !== 'processing') {
      // Clear timer if not processing
      if (timerIdRef.current) {
        console.log(`[ProcessingTime-${promptType}] Clearing timer due to status change: ${status}`);
        clearInterval(timerIdRef.current);
        timerIdRef.current = null;
      }
      return;
    }
    
    console.log(`[ProcessingTime-${promptType}] Starting timer, status: ${status}`);
    
    // Setup timer to update elapsed time every second
    timerIdRef.current = setInterval(() => {
      const currentElapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setElapsed(currentElapsed);
    }, 1000);
    
    // Fetch average processing time
    getAverageProcessingTime(promptType)
      .then(res => {
        if (res.hasEnoughData && res.recordCount >= MIN_SAMPLES) {
          setAvg(res.averageSeconds);
          setHasData(true);
          console.log(`[ProcessingTime-${promptType}] Avg processing time: ${res.averageSeconds}s`);
        }
      })
      .catch(err => {
        console.error(`[ProcessingTime-${promptType}] Error fetching average:`, err);
      });
    
    // Cleanup function
    return () => {
      console.log(`[ProcessingTime-${promptType}] Cleaning up timer`);
      if (timerIdRef.current) {
        clearInterval(timerIdRef.current);
        timerIdRef.current = null;
      }
    };
  }, [promptType, status]);

  // Render nothing if not processing
  if (status !== "processing") {
    return null;
  }

  // Debug logging
  const defaultAvg = 120; // 2 minutes default
  const actualAvg = avg || defaultAvg;
  const percentage = (elapsed / actualAvg) * 100;
  
  console.log(`[ProcessingTime-${promptType}] Debug: elapsed=${elapsed}s, avg=${actualAvg}s, percentage=${percentage.toFixed(2)}%`);

  // Calculate display values - FIXED CALCULATION
  const progressPct = Math.min(percentage, 100); // This is only for the progress bar width
  
  // Determine color state based on percentage - FIXED LOGIC
  let colorState;
  if (percentage < 100) {
    colorState = "green";
  } else if (percentage < 150) {
    colorState = "yellow";
  } else {
    colorState = "red";
  }
  
  console.log(`[ProcessingTime-${promptType}] Color state: ${colorState}`);
  
  const fmt = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;

  // Determine delay reasons based on color state
  const delayReasons =
    colorState === "yellow"
      ? ["Complex query", "Large HTML file", "AI service delay"]
      : colorState === "red"
      ? [
          "Extra processing",
          "Very complex query",
          "Very large HTML file",
          "AI service delay",
        ]
      : [];

  const showBanner = delayReasons.length > 0;

  // Directly use color variables for simplicity
  const colorClasses = {
    red: {
      border: "border-red-400",
      bg: "bg-red-50",
      text: "text-red-800",
      lightText: "text-red-700",
      timerText: "text-red-600",
      icon: "text-red-400",
      progressBar: "bg-red-500"
    },
    yellow: {
      border: "border-yellow-400",
      bg: "bg-yellow-50",
      text: "text-yellow-800",
      lightText: "text-yellow-700", 
      timerText: "text-yellow-600",
      icon: "text-yellow-400",
      progressBar: "bg-yellow-500"
    },
    green: {
      border: "border-green-400",
      bg: "bg-green-50",
      text: "text-green-800",
      lightText: "text-green-700",
      timerText: "text-green-600",
      icon: "text-green-400",
      progressBar: "bg-green-500"
    }
  };

  const colors = colorClasses[colorState];

  return (
    <div className="w-full max-w-md mx-auto mt-4">
      {/* Delay explanation banner with a completely reworked layout */}
      {showBanner && (
        <div className={`${colors.bg} border-l-4 ${colors.border} p-4 shadow-lg rounded mb-3`}>
          <div className="flex">
            {/* Warning Icon */}
            <div className="flex-shrink-0">
              <svg className={`h-5 w-5 ${colors.icon}`} viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            
            {/* Content Container with tight spacing */}
            <div className="ml-2">
              <p className={`text-sm font-medium ${colors.text}`}>Why it may be slower:</p>
              <div className="mt-1 space-y-1">
                {delayReasons.map((reason, index) => (
                  <div key={index} className="flex">
                    <span className={`${colors.lightText} mr-1`}>•</span>
                    <span className={`text-sm ${colors.lightText}`}>{reason}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Processing timer & progress bar */}
      <div className="bg-gray-100 rounded-lg p-4 shadow">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-gray-600">elapsed</span>
          <span className={`font-semibold ${colors.timerText}`}>{fmt(elapsed)}</span>
        </div>

        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            style={{ width: `${progressPct}%` }}
            className={`h-2 rounded-full ${colors.progressBar} transition-all`}
          />
        </div>

        {hasData && (
          <p className="text-center text-xs text-gray-500 mt-1">
            Average Duration (last 10 calls): {fmt(Math.round(actualAvg))}
          </p>
        )}
      </div>
    </div>
  );
}